import React from 'react'
import styled from 'styled-components'

import { Quad } from '@sketch-hq/sketch-web-renderer'
import { Rect } from '@sketch/inspector'
import { relativeMeasure } from '@sketch/utils'

import { COMPARE_TINT } from '../../compareTint'

const Outline = styled.div`
  position: absolute;
  pointer-events: none;

  /* Pink is the compared version, everywhere in this feature */
  box-shadow: inset 0 0 0 1px ${COMPARE_TINT.strong};
`

/**
 * A quad as a box plus a transform, so a rotated, flipped or sheared node is
 * outlined as the shape it is. Lengths go in the layout and only directions in
 * the transform, or the 1px outline would be scaled along with it.
 */
export const placeQuad = (quad: Quad, rootExtent: Rect) => {
  const { topLeft, topRight, bottomLeft } = quad

  const across = { x: topRight.x - topLeft.x, y: topRight.y - topLeft.y }
  const down = { x: bottomLeft.x - topLeft.x, y: bottomLeft.y - topLeft.y }

  const width = Math.hypot(across.x, across.y)
  const height = Math.hypot(down.x, down.y)

  if (!width || !height || !rootExtent.width || !rootExtent.height) return null

  return {
    left: `${relativeMeasure(topLeft.x, rootExtent.width)}%`,
    top: `${relativeMeasure(topLeft.y, rootExtent.height)}%`,
    width: `${relativeMeasure(width, rootExtent.width)}%`,
    height: `${relativeMeasure(height, rootExtent.height)}%`,
    // About the corner the box was placed from, so only the turn is left to do
    transformOrigin: '0 0',
    transform: `matrix(${across.x / width}, ${across.y / width}, ${
      down.x / height
    }, ${down.y / height}, 0, 0)`,
  }
}

interface OtherVersionOutlineProps {
  /** Where the other version of it belongs. */
  quad: Quad
  /** The scene root the position is taken as a percentage of. */
  rootExtent: Rect
}

/**
 * Marks where the other version of something belongs, for a reader looking at
 * this one. Always the compared version's colour, whichever of the two is
 * older: it is the version that is not on screen.
 */
export function OtherVersionOutline({
  quad,
  rootExtent,
}: OtherVersionOutlineProps) {
  const placed = placeQuad(quad, rootExtent)

  if (!placed) return null

  return <Outline data-testid="compare-other-version-outline" style={placed} />
}
